import { MonoBehavior } from './core/monoBehavior';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { Container, Graphics } from 'pixi.js';
import { addToUI } from './core/Playable2DRootContainer';
import { AssetLoader } from './gameUI/AssetLoader';
import gsap from 'gsap';

import { Layout } from './LayoutManager';
import { getScreenSize } from './core/screen';
import { Displays, DESKTOP_UI_WIDTH, DESKTOP_SIDEBAR_WIDTH } from 'constants/layout';
import { Time } from './core/pixiRenderer';

export class GamePreloader extends MonoBehavior {
    container: Container;
    gameBlockerContainer: Container;
    transition?: NodeJS.Timeout;
    activeLoaders = 0;
    loader?: AssetLoader;
    backgroundFill: Graphics;
    blockUI: Graphics;
    gameReady = false;
    settingsAreOpen = false;
    gameLoaded = false;
    targetAlpha = 0;
    constructor() {
        super();
        this.hide = this.hide.bind(this);
        this.show = this.show.bind(this);
        this.unblockView = this.unblockView.bind(this);
        this.blockView = this.blockView.bind(this);
        this.onOpenSettings = this.onOpenSettings.bind(this);
        this.onCloseSettings = this.onCloseSettings.bind(this);
        this.onGameReady = this.onGameReady.bind(this);
        this.onBackgroundLoaded = this.onBackgroundLoaded.bind(this);
        GameEvent.add(EventName.HIDE_LOADER, this.hide);
        GameEvent.add(EventName.SHOW_LOADER, this.show);
        GameEvent.add(EventName.OPEN_SETTINGS, this.onOpenSettings);
        GameEvent.add(EventName.HIDE_SETTINGS, this.onCloseSettings);
        GameEvent.add(EventName.GAME_READY, this.onGameReady);
        GameEvent.add(EventName.BACKGROUND_LOADED, this.onBackgroundLoaded);
        this.backgroundFill = new Graphics();
        this.backgroundFill.rect(-100, -100, 200, 200);
        this.backgroundFill.fill(0x000000);
        this.backgroundFill.alpha = 0;

        this.container = new Container();
        this.gameBlockerContainer = new Container();
        this.container.alpha = 0;
        this.container.addChild(this.backgroundFill);
        addToUI(this.container);
        addToUI(this.gameBlockerContainer);
        this.gameBlockerContainer.zIndex = 9999;

        this.blockUI = new Graphics();
        this.blockUI.rect(-100, -100, 200, 200);
        this.blockUI.fill(0x000000);
        this.blockUI.alpha = 0.00001;
        this.gameBlockerContainer.addChild(this.blockUI);
        this.container.zIndex = -1;
    }

    async init() {
        this.loader = await new AssetLoader(this.container);
        this.loader.setColor(0x000000);
        const { width, height } = getScreenSize();
        this.resize(width, height);
    }
    onGameReady() {
        setTimeout(() => {
            this.gameReady = true;
            const { width, height } = getScreenSize();
            this.resize(width, height);
            dispatchEvent(new CustomEvent('removePreloader'));
        }, 300);
        dispatchEvent(new CustomEvent('gameReady'));
    }
    onBackgroundLoaded() {
        if (!this.gameLoaded) {
            this.gameLoaded = true;
            this.blockView();
            const { width, height } = getScreenSize();
            this.resize(width, height);
        }

        if (this.loader) this.loader.setColor(0xffffff);
        gsap.to(this.backgroundFill, { alpha: 0.5 });
    }
    onOpenSettings() {
        this.settingsAreOpen = true;
    }
    onCloseSettings() {
        this.settingsAreOpen = false;
    }
    show() {
        this.targetAlpha = 1;
        this.blockView();
        const { width, height } = getScreenSize();
        this.resize(width, height);
        this.activeLoaders++;
    }
    hide(duration = 0.5) {
        this.activeLoaders--;
        if (this.activeLoaders <= 0 || duration === 0) {
            this.activeLoaders = 0;
        } else {
            return;
        }
        if (duration === 0) {
            this.targetAlpha = 0;
            this.container.alpha = 0;
            this.unblockView();
        } else {
            this.targetAlpha = 0;
        }
    }
    unblockView() {
        this.gameBlockerContainer.eventMode = 'none';
    }
    blockView() {
        this.gameBlockerContainer.eventMode = 'static';
    }
    update(): void {
        if (this.targetAlpha === 0 && this.gameBlockerContainer.eventMode === 'static') {
            this.unblockView();
        }
        if (this.targetAlpha === this.container.alpha) return;
        this.container.alpha += Time.deltaTime * 0.05 * (this.targetAlpha === 1 ? 1 : -1);
        if (this.container.alpha <= 0) {
            this.container.alpha = 0;
            this.unblockView();
        } else if (this.container.alpha >= 1) {
            this.container.alpha = 1;
        }
    }
    resize(width: number, height: number): void {
        this.backgroundFill.width = width;
        this.backgroundFill.height = height;
        this.blockUI.width = width;
        this.blockUI.height = height;
        this.blockUI.position.set(width / 2, height / 2);
        this.container.position.set(width / 2, height / 2);

        switch (Layout.getDisplay()) {
            case Displays.MOBILE:
                this.container.zIndex = 9999;
                break;
            default:
                this.container.zIndex = -1;
                break;
        }
        if (!this.loader) return;
        const display = Layout.getDisplay();

        if (display === Displays.DESKTOP && this.gameReady) {
            if (this.settingsAreOpen) {
                this.loader.container.position.x = -DESKTOP_UI_WIDTH / 2;
            } else {
                this.loader.container.position.x = -DESKTOP_SIDEBAR_WIDTH / 2;
            }
        }
    }
}
